/**
 * Contact-Specific Actions
 * Actions defined specifically for the contacts entity
 * Strongly typed to Contacts row type
 * Uses shared builders to eliminate duplication
 */

import type { Contacts } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import { ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  universalDeleteAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalMakeTicketAction,
  universalLogTimeEntryAction,
  universalViewCompanyAction,
  universalCreateTaskAction,
  universalScheduleMeetingAction,
  universalLinkCompanyAction,
  universalEnrichAction,
  universalViewDetailsAction,
  universalViewSupportInboxAction,
  universalConfigureAutomatedTasksAction,
  universalViewAuditHistoryAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createCallAction,
  createSendTextMessageAction,
  createGenerateContractAction,
  createSmAgreementAction,
  createGrantAgreementAction,
  createFillChecklistsAction,
  createManageAccessAction,
  createConsolidateAction,
  createResearchCompanyAction,
  createFindDecisionMakersAction,
  createLogDealAction,
} from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";
import { createEnrollInSequenceAction } from "@/lib/actions/definitions/shared/sequence-actions";

/**
 * Complete contact actions array
 * Combines universal and contact-specific actions in the correct order
 * Order matches the original contact-detail-actions.ts for behavior preservation
 */
export const contactDetailActions: DetailActionStrict<Contacts>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Contacts>(),

  // PRIMARY ACTIONS (Header)
  createStatusAction<Contacts>({ primary: false }),
  // No Edit action — fields are edited inline on the detail page; the old
  // modal-based edit was an unmounted no-op (see deal-actions for the full note).
  universalSendEmailAction<Contacts>({ primary: true }),
  universalLogActivityAction<Contacts>({ primary: true }),

  // TABLE-ONLY ACTIONS
  universalViewDetailsAction<Contacts>("viewContact"),

  // SECONDARY ACTIONS (Menu)
  createCallAction<Contacts>(),
  // Admin-only while rollout stabilizes. Uses cross-entity resolver.
  createSendTextMessageAction<Contacts>({
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  }),
  universalCreateTaskAction<Contacts>(),
  createLogDealAction<Contacts>(),
  // Sales cadence — gated to the rollout allowlist at useActionFiltering (by email).
  createEnrollInSequenceAction<Contacts>({
    entity: "contact",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  }),
  universalMakeTicketAction<Contacts>(),
  universalLogTimeEntryAction<Contacts>(),
  universalScheduleMeetingAction<Contacts>(),
  createGenerateContractAction<Contacts>(),
  createSmAgreementAction<Contacts>(),
  createGrantAgreementAction<Contacts>(),
  createFillChecklistsAction<Contacts>(),
  createFillChecklistsAction<Contacts>({
    id: "fill-client-checklist",
    label: "Fill Client Checklist",
    action: "fillClientChecklist",
  }),
  createFillChecklistsAction<Contacts>({
    id: "fill-telecom-checklist",
    label: "Fill Telecom Checklist",
    action: "fillTelecomChecklist",
  }),
  universalViewCompanyAction<Contacts>(),
  // Cross-entity: walks contact → company to open the company's support inbox.
  universalViewSupportInboxAction<Contacts>(),
  universalLinkCompanyAction<Contacts>(),
  universalEnrichAction<Contacts>(),
  universalConfigureAutomatedTasksAction<Contacts>(),
  createSendToSlackAction<Contacts>(),
  createSendToSalesChannelAction<Contacts>(),
  createOpenSlackChannelAction<Contacts>(),
  universalDeleteAction<Contacts>(),
  createManageAccessAction<Contacts>(),
  createConsolidateAction<Contacts>(),
  createResearchCompanyAction<Contacts>(),
  createFindDecisionMakersAction<Contacts>(),
  // History & Audit — read-only forensic view + per-event restore. Staff only;
  // registered here so it reaches BOTH the ⋯ button and right-click on every
  // surface that renders a contact (ENTITY_TABLE_ROW_ACTIONS).
  universalViewAuditHistoryAction<Contacts>(),
];
